/**
 * Adaptadores entre los views HTTP del backend bank-file (repo payroll) y el
 * modelo que consumen las vistas. Es el UNICO lugar que conoce los shapes
 * crudos del backend; si el contrato cambia, solo se toca este archivo.
 */
import {
  BankTemplate,
  BankTemplateSummary,
  CanonicalSuggestion,
  DryRunResult,
  EditorSupport,
  FALLBACK_SUGGESTIONS,
  ValidationResult
} from './types'

/* ------------------------------------------------------------------------- */
/* Shapes crudos del backend (views serializados sin envoltorio)              */
/* ------------------------------------------------------------------------- */

/* GET / — BankTemplateListView */
export interface RawListItem {
  bankTemplateId: string
  name: string
  status: string
  countryId: string
  originBankId: string
  destinationBankIds: string[]
  publishedVersionNumber: number | null
  draftVersionNumber: number | null
  updatedAt: string
}

/* GET /:id — BankTemplateView (el router ya convierte definition -> yaml) */
export interface RawDetail {
  bankTemplateId: string
  name: string
  countryId: string
  originBankId: string
  destinationBankIds: string[]
  status: string
  workingVersion: {
    versionNumber: number
    status: string
    yaml: string
  } | null
  versions: {
    versionNumber: number
    status: string
    publishedAt: string | null
  }[]
  createdAt: string
  updatedAt: string
}

/* POST /validate — ValidationView (los items se llaman "errors") */
export interface RawValidation {
  valid: boolean
  errors: {
    code: string
    path: string
    message: string
    line?: number
    column?: number
  }[]
}

/* POST /:id/dry-run — DryRunView */
export interface RawDryRun {
  fileName: string
  format: string
  extension: string
  encoding: string
  content: string
  warnings: string[]
}

/* GET /editor-support — TemplateEditorSupport */
export interface RawEditorSupport {
  canonicalPaths?: readonly string[]
  functions?: readonly string[]
}

/* POST / — CreateBankTemplateResult */
export interface RawCreateResult {
  bankTemplateId: string
}

/* ------------------------------------------------------------------------- */
/* Adaptadores                                                                */
/* ------------------------------------------------------------------------- */

/* Fecha ISO -> fecha corta que muestra el listado */
const toShortDate = (iso: string): string => (iso ?? '').slice(0, 10)

/**
 * Item del listado. Los catalogos resuelven los UUID a nombres visibles;
 * si el id no esta en el catalogo se muestra el id como ultimo recurso.
 */
export const toSummary = (
  raw: RawListItem,
  countryNames: Map<string, string>,
  bankNames: Map<string, string>
): BankTemplateSummary => ({
  id: raw.bankTemplateId,
  name: raw.name,
  bankName: bankNames.get(raw.originBankId) ?? raw.originBankId,
  countryId: raw.countryId,
  country: countryNames.get(raw.countryId) ?? raw.countryId,
  publishedVersion: raw.publishedVersionNumber,
  hasDraft: raw.draftVersionNumber !== null,
  active: raw.status === 'ACTIVE',
  updatedAt: toShortDate(raw.updatedAt)
})

/**
 * Detalle. `workingVersion` es el borrador si existe; si no, la publicada.
 * La version publicada visible se deduce de versions[].status.
 */
export const toTemplate = (raw: RawDetail): BankTemplate => {
  const working = raw.workingVersion
  const isDraft = working?.status === 'DRAFT'
  return {
    id: raw.bankTemplateId,
    name: raw.name,
    countryId: raw.countryId,
    originBankId: raw.originBankId,
    destinationBankIds: raw.destinationBankIds ?? [],
    active: raw.status === 'ACTIVE',
    publishedVersion:
      raw.versions?.find(version => version.status === 'PUBLISHED')
        ?.versionNumber ?? null,
    draftYaml: isDraft ? working.yaml : null,
    publishedYaml: !isDraft && working ? working.yaml : null,
    updatedAt: toShortDate(raw.updatedAt)
  }
}

/**
 * Issues de validacion. El backend no distingue severidad (todo es error);
 * el path (Template.records[0]...) se antepone al mensaje cuando existe.
 */
export const toValidationResult = (raw: RawValidation): ValidationResult => ({
  valid: raw.valid,
  issues: (raw.errors ?? []).map(issue => ({
    severity: 'error',
    message: issue.path ? `${issue.path}: ${issue.message}` : issue.message,
    line: issue.line
  }))
})

export const toDryRunResult = (raw: RawDryRun): DryRunResult => ({
  fileName: raw.fileName,
  format: raw.format,
  extension: raw.extension,
  encoding: raw.encoding ?? 'utf-8',
  content: raw.content ?? '',
  warnings: raw.warnings ?? [],
  /* En binarios (base64) no hay lineas legibles que previsualizar */
  lines:
    (raw.encoding ?? 'utf-8') === 'utf-8'
      ? (raw.content ?? '').split(/\r?\n/)
      : []
})

/**
 * Sugerencias del editor a partir de canonicalPaths + functions del backend.
 * Si el shape no trae nada usable se cae al catalogo estatico local.
 */
export const toEditorSupport = (raw: RawEditorSupport): EditorSupport => {
  const paths: CanonicalSuggestion[] = (raw.canonicalPaths ?? [])
    .filter(path => !path.includes('*'))
    .map(path => ({ value: path, hint: 'ruta' }))
  const functions: CanonicalSuggestion[] = (raw.functions ?? []).map(name => ({
    value: `${name}(`,
    hint: 'función'
  }))
  const suggestions = [...paths, ...functions]
  return {
    suggestions: suggestions.length > 0 ? suggestions : FALLBACK_SUGGESTIONS
  }
}
